/**
 * Urban Brain Health Points System (UBHPS) Scoring Engine.
 *
 * 100 points across 8 pathways, modeled on LEED-ND / WELL Community.
 * 5 mandatory preconditions + features earning 1-3 points each.
 */

// Pathway caps (total = 100)
export const PATHWAY_CAPS = {
  nature: 18,
  air_quality: 18,
  movement: 15,
  enrichment: 13,
  noise: 12,
  daylight: 10,
  visual: 7,
  wayfinding: 7,
};

// Preconditions (mandatory, 0 pts)
export const PRECONDITIONS = [
  { id: 'UP1', pathway: 'air_quality', label: 'PM2.5 < 35 µg/m³ annual average', threshold: '<35 µg/m³' },
  { id: 'UP2', pathway: 'noise', label: 'No zone >75 dB Lden without mitigation plan', threshold: '<75 dB' },
  { id: 'UP3', pathway: 'nature', label: 'Minimum 10% tree canopy coverage', threshold: '>=10%' },
  { id: 'UP4', pathway: 'movement', label: 'Pedestrian access to all public spaces (ADA equivalent)', threshold: 'manual' },
  { id: 'UP5', pathway: 'movement', label: 'Emergency vehicle access maintained', threshold: 'manual' },
];

// Feature catalog
export const FEATURES = [
  // Nature / Green Infrastructure (18 pts)
  { id: 'UN1', pathway: 'nature', points: 3, label: 'Tree canopy >=25% of zone area', autoCheck: z => (z.treeCanopyPct || 0) >= 25 },
  { id: 'UN2', pathway: 'nature', points: 3, label: 'Tree canopy >=40% of zone area', autoCheck: z => (z.treeCanopyPct || 0) >= 40 },
  { id: 'UN3', pathway: 'nature', points: 3, label: '>=80% of residents within 300m of green space', autoCheck: z => (z.greenSpaceAccess300m || 0) >= 80 },
  { id: 'UN4', pathway: 'nature', points: 2, label: 'Permeable surfaces >=30% of zone area', autoCheck: z => (z.permeableSurfacePct || 0) >= 30 },
  { id: 'UN5', pathway: 'nature', points: 2, label: 'Connected green corridor / linear park', autoCheck: null },
  { id: 'UN6', pathway: 'nature', points: 2, label: 'Native/pollinator planting in public spaces', autoCheck: null },
  { id: 'UN7', pathway: 'nature', points: 1, label: 'Community garden or urban agriculture', autoCheck: null },
  { id: 'UN8', pathway: 'nature', points: 2, label: 'Water feature or stream daylighting', autoCheck: null },

  // Air Quality (18 pts)
  { id: 'UA1', pathway: 'air_quality', points: 3, label: 'PM2.5 < 12 µg/m³ annual average', autoCheck: z => (z.pm25Annual || 999) < 12 },
  { id: 'UA2', pathway: 'air_quality', points: 3, label: 'PM2.5 < 8 µg/m³ annual average', autoCheck: z => (z.pm25Annual || 999) < 8 },
  { id: 'UA3', pathway: 'air_quality', points: 3, label: '>=80% of zone >100m from major traffic', autoCheck: z => (z.pctZoneOver100mFromTraffic || 0) >= 80 },
  { id: 'UA4', pathway: 'air_quality', points: 2, label: 'Green buffer rows between traffic and pedestrians', autoCheck: z => (z.greenBufferScore || 0) >= 0.6 },
  { id: 'UA5', pathway: 'air_quality', points: 2, label: 'EV charging infrastructure at >=10% of parking', autoCheck: null },
  { id: 'UA6', pathway: 'air_quality', points: 2, label: 'Low-emission zone or traffic calming measures', autoCheck: null },
  { id: 'UA7', pathway: 'air_quality', points: 1, label: 'Air quality monitoring stations visible to public', autoCheck: null },
  { id: 'UA8', pathway: 'air_quality', points: 2, label: 'Industrial/pollution source mitigation plan', autoCheck: null },

  // Movement / Walkability (15 pts)
  { id: 'UM1', pathway: 'movement', points: 3, label: 'Walk Score >=70', autoCheck: z => (z.walkScore || 0) >= 70 },
  { id: 'UM2', pathway: 'movement', points: 3, label: 'Walk Score >=90', autoCheck: z => (z.walkScore || 0) >= 90 },
  { id: 'UM3', pathway: 'movement', points: 2, label: 'Dedicated bike lanes on >=50% of streets', autoCheck: z => (z.bikeInfrastructure || 0) >= 0.5 },
  { id: 'UM4', pathway: 'movement', points: 2, label: 'Block permeability >=0.6 (multiple access points)', autoCheck: z => (z.blockPermeability || 0) >= 0.6 },
  { id: 'UM5', pathway: 'movement', points: 2, label: 'Pedestrian-priority streets or shared spaces', autoCheck: null },
  { id: 'UM6', pathway: 'movement', points: 2, label: 'Public transit stop within 400m of all residences', autoCheck: null },
  { id: 'UM7', pathway: 'movement', points: 1, label: 'Outdoor fitness stations or exercise circuit', autoCheck: null },

  // Enrichment / Activation (13 pts)
  { id: 'UE1', pathway: 'enrichment', points: 2, label: 'Mixed-use index >=0.5 (Shannon diversity)', autoCheck: z => (z.mixedUseIndex || 0) >= 0.5 },
  { id: 'UE2', pathway: 'enrichment', points: 2, label: 'Ground-plane activation >=50% of street frontage', autoCheck: z => (z.groundPlaneActivationPct || 0) >= 50 },
  { id: 'UE3', pathway: 'enrichment', points: 2, label: 'Social infrastructure >=2 spaces per 1000 residents', autoCheck: z => (z.socialInfraPerThousand || 0) >= 2 },
  { id: 'UE4', pathway: 'enrichment', points: 2, label: 'Cultural/civic amenity within zone', autoCheck: null },
  { id: 'UE5', pathway: 'enrichment', points: 2, label: 'Public art program or rotating installations', autoCheck: null },
  { id: 'UE6', pathway: 'enrichment', points: 2, label: 'Multigenerational programming spaces', autoCheck: null },
  { id: 'UE7', pathway: 'enrichment', points: 1, label: 'Farmers market or pop-up retail program', autoCheck: null },

  // Noise Environment (12 pts)
  { id: 'UNO1', pathway: 'noise', points: 3, label: '>=30% of zone designated quiet areas (<50 dB)', autoCheck: z => (z.pctQuietAreas || 0) >= 30 },
  { id: 'UNO2', pathway: 'noise', points: 2, label: '<20% of zone exceeds 65 dB Lden', autoCheck: z => (z.pctZoneOver65dB || 0) < 20 },
  { id: 'UNO3', pathway: 'noise', points: 2, label: 'Traffic noise barriers on major corridors', autoCheck: null },
  { id: 'UNO4', pathway: 'noise', points: 2, label: 'Water features or natural soundscape elements', autoCheck: z => (z.soundEnvironmentQuality || 0) >= 0.6 },
  { id: 'UNO5', pathway: 'noise', points: 2, label: 'Building setback from noise sources', autoCheck: null },
  { id: 'UNO6', pathway: 'noise', points: 1, label: 'Noise monitoring and public information system', autoCheck: null },

  // Daylight / Solar Access (10 pts)
  { id: 'UD1', pathway: 'daylight', points: 3, label: 'Sky view factor >=0.4 across zone', autoCheck: z => (z.skyViewFactor || 0) >= 0.4 },
  { id: 'UD2', pathway: 'daylight', points: 3, label: '>=4 hours direct sunlight at ground plane (equinox)', autoCheck: z => (z.sunlightHoursGroundPlane || 0) >= 4 },
  { id: 'UD3', pathway: 'daylight', points: 2, label: 'Solar-optimized street orientation', autoCheck: null },
  { id: 'UD4', pathway: 'daylight', points: 2, label: 'Building height-to-width ratio preserves light access', autoCheck: null },

  // Visual Environment (7 pts)
  { id: 'UV1', pathway: 'visual', points: 2, label: 'Facade diversity index >=0.5', autoCheck: z => (z.facadeDiversityIndex || 0) >= 0.5 },
  { id: 'UV2', pathway: 'visual', points: 2, label: 'Streetscape visual interest score >=0.5', autoCheck: z => (z.streetscapeVisualInterest || 0) >= 0.5 },
  { id: 'UV3', pathway: 'visual', points: 2, label: 'Varied building heights and setbacks', autoCheck: null },
  { id: 'UV4', pathway: 'visual', points: 1, label: 'Avoidance of monotonous repetitive facades', autoCheck: null },

  // Wayfinding / Legibility (7 pts)
  { id: 'UW1', pathway: 'wayfinding', points: 2, label: 'Street network intelligibility >=0.5', autoCheck: z => (z.streetIntelligibility || 0) >= 0.5 },
  { id: 'UW2', pathway: 'wayfinding', points: 2, label: 'Landmark visibility at key decision points', autoCheck: z => (z.landmarkScore || 0) >= 0.5 },
  { id: 'UW3', pathway: 'wayfinding', points: 2, label: 'Wayfinding signage system throughout zone', autoCheck: null },
  { id: 'UW4', pathway: 'wayfinding', points: 1, label: 'Digital/interactive wayfinding at key locations', autoCheck: null },
];

export const FEATURE_MAP = Object.fromEntries(FEATURES.map(f => [f.id, f]));

/**
 * Check preconditions against a zone.
 */
export function checkPreconditions(zone) {
  return {
    UP1: (zone.pm25Annual || 999) < 35,
    UP2: (zone.pctZoneOver65dB || 0) < 75 || true, // simplified — need mitigation plan check
    UP3: (zone.treeCanopyPct || 0) >= 10,
    UP4: true, // Manual
    UP5: true, // Manual
  };
}

/**
 * Auto-detect features from zone measurements.
 */
export function autoDetectFeatures(zone) {
  const detected = {};
  for (const f of FEATURES) {
    if (f.autoCheck) {
      try { detected[f.id] = f.autoCheck(zone); }
      catch { detected[f.id] = false; }
    }
  }
  return detected;
}

/**
 * Score a single zone.
 */
export function scoreZone(zone, featureStatuses = {}) {
  const preconditions = checkPreconditions(zone);
  const allPass = Object.values(preconditions).every(v => v);
  const auto = autoDetectFeatures(zone);

  const pathwayPoints = {};
  for (const pw of Object.keys(PATHWAY_CAPS)) {
    pathwayPoints[pw] = 0;
  }

  const features = FEATURES.map(f => {
    const manualStatus = featureStatuses[f.id] || 'not_pursuing';
    const autoResult = auto[f.id];

    let status, autoDetected;
    if (manualStatus === 'earned') {
      status = 'earned';
      autoDetected = false;
    } else if (autoResult === true) {
      status = 'earned';
      autoDetected = true;
    } else {
      status = manualStatus;
      autoDetected = false;
    }

    let earnedPts = status === 'earned' ? f.points : 0;
    if (pathwayPoints[f.pathway] + earnedPts > PATHWAY_CAPS[f.pathway]) {
      earnedPts = Math.max(0, PATHWAY_CAPS[f.pathway] - pathwayPoints[f.pathway]);
    }
    pathwayPoints[f.pathway] += earnedPts;

    return {
      featureId: f.id,
      status,
      points: earnedPts,
      label: f.label,
      pathway: f.pathway,
      autoDetected,
    };
  });

  const totalPoints = Object.values(pathwayPoints).reduce((a, b) => a + b, 0);
  const tier = allPass ? getCertificationTier(totalPoints) : 'none';

  return {
    zoneId: zone.id,
    zoneName: zone.name,
    preconditionsPassed: allPass,
    preconditionResults: preconditions,
    totalPoints,
    pathwayPoints,
    pathwayCaps: PATHWAY_CAPS,
    tier,
    features,
  };
}

/**
 * Score an entire project (multi-zone, LEED-style tiered thresholds).
 */
export function scoreProject(zonesWithFeatures) {
  const numZones = zonesWithFeatures.length;
  if (numZones === 0) {
    return {
      totalPoints: 0, pathwayPoints: {}, pathwayCaps: PATHWAY_CAPS,
      tier: 'none', allPreconditionsPass: false, projectFeatures: [],
      zoneScores: [], avgScore: 0,
    };
  }

  const zoneScores = zonesWithFeatures.map(({ zone, featureStatuses }) =>
    scoreZone(zone, featureStatuses)
  );

  const allPreconditionsPass = zoneScores.every(zs => zs.preconditionsPassed);

  const featureEarnedCounts = {};
  for (const f of FEATURES) {
    featureEarnedCounts[f.id] = zoneScores.filter(zs => {
      const sf = zs.features.find(feat => feat.featureId === f.id);
      return sf && sf.status === 'earned';
    }).length;
  }

  const pathwayPoints = {};
  for (const pw of Object.keys(PATHWAY_CAPS)) {
    pathwayPoints[pw] = 0;
  }

  const projectFeatures = FEATURES.map(f => {
    const earnedCount = featureEarnedCounts[f.id] || 0;
    const pctEarned = earnedCount / numZones;
    const isAutoDetectable = f.autoCheck !== null;

    let earnedPts = 0;
    let complianceLevel = 'none';

    if (isAutoDetectable) {
      if (pctEarned >= 0.75) {
        earnedPts = f.points;
        complianceLevel = 'full';
      } else if (pctEarned >= 0.50) {
        earnedPts = Math.ceil(f.points / 2);
        complianceLevel = 'partial';
      }
    } else {
      if (earnedCount > 0) {
        earnedPts = f.points;
        complianceLevel = 'full';
      }
    }

    if (pathwayPoints[f.pathway] + earnedPts > PATHWAY_CAPS[f.pathway]) {
      earnedPts = Math.max(0, PATHWAY_CAPS[f.pathway] - pathwayPoints[f.pathway]);
    }
    pathwayPoints[f.pathway] += earnedPts;

    return {
      featureId: f.id, label: f.label, pathway: f.pathway, maxPoints: f.points,
      earnedPts, earnedCount, totalZones: numZones,
      pctCompliant: Math.round(pctEarned * 100), complianceLevel, isAutoDetectable,
    };
  });

  const totalPoints = Object.values(pathwayPoints).reduce((a, b) => a + b, 0);
  const tier = allPreconditionsPass ? getCertificationTier(totalPoints) : 'none';

  return {
    totalPoints, pathwayPoints, pathwayCaps: PATHWAY_CAPS, tier,
    allPreconditionsPass, projectFeatures, zoneScores, avgScore: totalPoints,
  };
}

export function getCertificationTier(score) {
  score = Math.round(score);
  if (score >= 75) return 'platinum';
  if (score >= 60) return 'gold';
  if (score >= 45) return 'silver';
  if (score >= 30) return 'bronze';
  return 'none';
}
